import { useState } from "react";
import { useSearchParams } from "react-router-dom";

import type { AuthenticationPasswordRequestData } from "../../schemas";
import { LoginTokenLink } from "../../components";
import { useAuthentication, useIdentityClient, useToken } from "../../hooks";

import { LoginPasswordTemplate } from "./login-password.template";

type SignInFunction = (data: AuthenticationPasswordRequestData) => Promise<void>;

export const LoginPassword = () => {
	const [searchParams] = useSearchParams();
	const username = searchParams.get("username");

	const { applicationName } = useAuthentication();
	const { setToken } = useToken();

	const [error, setError] = useState(false);
	const [ssoError, setSsoError] = useState(false);
	const [submitting, setSubmitting] = useState(false);

	const { client } = useIdentityClient();

	const handleSSOSignIn: SignInFunction = async (data: AuthenticationPasswordRequestData) => {
		const result = await client.GetUserSsoInfo(data.username, applicationName);

		if (!result.success || !result.data || !result.data.isSsoEnabled || !result.data.redirectUrl) {
			setSsoError(true);
			return;
		}

		window.location.replace(result.data.redirectUrl);
	};

	const handleDefaultSignIn: SignInFunction = async (data: AuthenticationPasswordRequestData) => {
		const response = await client.requestToken(data.username, data.password || "");

		if (!response.success || !response.data || !response.data.access_token) {
			setError(true);
			return;
		}

		setToken(response.data.access_token);
	};

	const handleSubmit = async (data: AuthenticationPasswordRequestData) => {
		if (submitting) {
			return;
		}

		setSubmitting(true);
		setError(false);
		setSsoError(false);

		try {
			if (data.password) {
				await handleDefaultSignIn(data);
				return;
			}

			await handleSSOSignIn(data);
		} finally {
			setSubmitting(false);
		}
	};

	return (
		<>
			<LoginPasswordTemplate
				onSubmit={handleSubmit}
				defaultUsername={username || ""}
				isError={error}
				isSsoError={ssoError}
			/>
			<LoginTokenLink />
		</>
	);
};
